import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslator } from 'tradera/lang/translate';
import { memberIdentificationClient } from 'tradera/utils/http';
import { LocalLoader } from 'tradera/components/loader/loader';
import { MitId } from 'tradera/components/international-identification-modal/mit-id';
import { MitIdLogo } from './mit-id-logo';
import { PayPal } from 'tradera/components/international-identification-modal/paypal';
import { PayPalLogo } from './paypal-logo';
import { Idin } from 'tradera/components/international-identification-modal/idin';
import { IdinLogo } from 'tradera/components/international-identification-modal/idin-logo';
import styles from './international-identification-modal.module.scss';
import { CardVerification } from './card-verification';
import { logger } from 'packages/logger';
import { Button } from '../atoms/button/button';
import { ScrollableModal } from '../scrollable-modal/scrollable-modal';
import { SignicatScanning } from 'tradera/components/international-identification-modal/signicat-scanning';
import { HeaderWithCloseButton } from 'tradera/components/scrollable-modal/header-with-close-button';
import ArrowLeftIcon from '@tradera/blueprint/theme/icons2/arrow-left.svg?jsx';
import VerificationIcon from 'tradera/blueprint/theme/icons-colored/verification.svg?jsx';
import { InfoBox } from 'tradera/components/info-box/info-box';

export const IDENTIFICATION_TYPES = {
    MITID: 'MitId',
    CARD: 'Card',
    SIGNICATSCANNING: 'SignicatScanning',
    PAYPAL: 'PayPal',
    IDIN: 'iDIN'
};

type InternationalIdentificationModalProps = {
    heading: string;
    introText: string;
    onCloseModal: () => void;
    onTop?: boolean;
    onVerifyComplete: () => void;
    purpose: string;
    errorText: string | null;
    isResponseLoading: boolean;
};

export const InternationalIdentificationModal = ({
    heading,
    introText,
    onCloseModal,
    onTop,
    onVerifyComplete,
    purpose,
    errorText,
    isResponseLoading
}: InternationalIdentificationModalProps) => {
    const { t } = useTranslator();
    const abortController = useRef<AbortController>();

    const [validIdentifications, setValidIdentifications] = useState<
        string[] | null
    >(null);
    const [error, setError] = useState(null);
    const [isHidden, setIsHidden] = useState(false);
    const [selectedIdentification, setSelectedIdentification] = useState<
        string | null
    >(null);
    const [
        isSelectedIdentificationLoading,
        setSelectedIdentificationIsLoading
    ] = useState(true);

    useEffect(() => {
        memberIdentificationClient
            .query<{ validIdentifications: string[] }>(
                `getvalididentifications?purpose=${purpose}`
            )
            .then((data) => {
                setValidIdentifications(data.validIdentifications);
                return;
            })
            .catch(setError);
    }, [setValidIdentifications, purpose]);

    useEffect(() => {
        abortController.current = new AbortController();
        return () => {
            abortController.current?.abort();
        };
    }, []);

    const onCancel = useCallback(() => {
        abortController.current?.abort();
        abortController.current = new AbortController();
        setSelectedIdentification(null);
        setSelectedIdentificationIsLoading(true);
    }, []);

    const selectedIdentificationGui = selectedIdentification
        ? {
              [IDENTIFICATION_TYPES.CARD]: (signal: AbortSignal) => (
                  <CardVerification
                      onCardVerified={onVerifyComplete}
                      setIsHidden={setIsHidden}
                      setSelectedIdentificationIsLoading={
                          setSelectedIdentificationIsLoading
                      }
                      signal={signal}
                  />
              ),
              [IDENTIFICATION_TYPES.SIGNICATSCANNING]: (
                  signal: AbortSignal
              ) => (
                  <SignicatScanning
                      onVerifyComplete={onVerifyComplete}
                      purpose={purpose}
                      setSelectedIdentificationIsLoading={
                          setSelectedIdentificationIsLoading
                      }
                      signal={signal}
                  />
              ),
              [IDENTIFICATION_TYPES.MITID]: (signal: AbortSignal) => (
                  <MitId
                      purpose={purpose}
                      setSelectedIdentificationIsLoading={
                          setSelectedIdentificationIsLoading
                      }
                      signal={signal}
                  />
              ),
              [IDENTIFICATION_TYPES.PAYPAL]: (signal: AbortSignal) => (
                  <PayPal
                      purpose={purpose}
                      setSelectedIdentificationIsLoading={
                          setSelectedIdentificationIsLoading
                      }
                      signal={signal}
                  />
              ),
              [IDENTIFICATION_TYPES.IDIN]: (signal: AbortSignal) => (
                  <Idin
                      purpose={purpose}
                      setSelectedIdentificationIsLoading={
                          setSelectedIdentificationIsLoading
                      }
                      signal={signal}
                  />
              )
          }[selectedIdentification]
        : null;

    const showGlobalLoader =
        (selectedIdentificationGui && isSelectedIdentificationLoading) ||
        (!selectedIdentificationGui && isResponseLoading);
    const showLocalLoader =
        !showGlobalLoader && !validIdentifications && !error;
    return (
        <ScrollableModal
            show={!isHidden}
            onTop={onTop}
            mobileBottomSheet
            bottomSheetFullHeight={true}
            padding={
                !(
                    !!selectedIdentificationGui &&
                    !isSelectedIdentificationLoading
                )
            }
            header={
                <HeaderWithCloseButton
                    onClose={onCloseModal}
                    title={selectedIdentificationGui ? heading : null}
                    border={!!selectedIdentificationGui}
                    menu={
                        selectedIdentificationGui && (
                            <Button
                                theme="text"
                                size="md"
                                round
                                onClick={onCancel}
                                icon={
                                    <ArrowLeftIcon className="icon icon-sm" />
                                }
                            />
                        )
                    }
                />
            }
            onClose={onCloseModal}>
            {(!selectedIdentificationGui ||
                isSelectedIdentificationLoading) && (
                <div className="pb-8 align-items-center position-relative">
                    {showGlobalLoader && (
                        <LocalLoader
                            className={`${styles.internationalIdentificationModalOpacity}`}
                        />
                    )}
                    {errorText ? (
                        <InfoBox
                            className="mb-3"
                            danger
                            header={t(
                                'international_identification_validation_title'
                            )}>
                            {errorText}
                        </InfoBox>
                    ) : null}

                    <div className="text-center">
                        <VerificationIcon width={100} className="mb-3" />
                        <h1 className="heading-london text-styled">
                            {heading}
                        </h1>
                        <p className="mb-3">{introText}</p>
                        {showLocalLoader && (
                            <div
                                className="position-relative w-100"
                                style={{ minHeight: '5vh' }}>
                                <LocalLoader />
                            </div>
                        )}
                        {validIdentifications &&
                            validIdentifications.map(
                                (identification, index) => {
                                    let element = null;
                                    switch (identification) {
                                        case IDENTIFICATION_TYPES.MITID:
                                            element = (
                                                <Button
                                                    theme="primary"
                                                    fluid
                                                    className="mb-2"
                                                    onClick={() =>
                                                        setSelectedIdentification(
                                                            IDENTIFICATION_TYPES.MITID
                                                        )
                                                    }
                                                    fixedIcon={true}
                                                    icon={
                                                        <MitIdLogo
                                                            className="mr-2"
                                                            color="white"
                                                        />
                                                    }>
                                                    {t(
                                                        'international_identification_mitid_button'
                                                    )}
                                                </Button>
                                            );
                                            break;
                                        case IDENTIFICATION_TYPES.IDIN:
                                            element = (
                                                <Button
                                                    theme="primary"
                                                    fluid
                                                    className="mb-2"
                                                    onClick={() =>
                                                        setSelectedIdentification(
                                                            IDENTIFICATION_TYPES.IDIN
                                                        )
                                                    }
                                                    fixedIcon={true}
                                                    icon={
                                                        <IdinLogo className="mr-1" />
                                                    }>
                                                    {t(
                                                        'international_identification_idin_button'
                                                    )}
                                                </Button>
                                            );
                                            break;
                                        case IDENTIFICATION_TYPES.SIGNICATSCANNING:
                                            purpose === 'Payment'
                                                ? (element = (
                                                      <>
                                                          {validIdentifications.includes(
                                                              IDENTIFICATION_TYPES.MITID
                                                          ) ||
                                                          validIdentifications.includes(
                                                              IDENTIFICATION_TYPES.IDIN
                                                          )
                                                              ? t(
                                                                    'international_identification_no_fit'
                                                                )
                                                              : t(
                                                                    'international_identification_to_sell'
                                                                )}
                                                          &nbsp;
                                                          <Button
                                                              theme="link"
                                                              className="my-1"
                                                              onClick={() =>
                                                                  setSelectedIdentification(
                                                                      IDENTIFICATION_TYPES.SIGNICATSCANNING
                                                                  )
                                                              }>
                                                              <b>
                                                                  {t(
                                                                      'international_identification_signicat_scanning_button'
                                                                  )}
                                                              </b>
                                                          </Button>
                                                      </>
                                                  ))
                                                : (element = (
                                                      <Button
                                                          theme="primary"
                                                          variant={
                                                              index !== 0
                                                                  ? 'outline'
                                                                  : undefined
                                                          }
                                                          fluid
                                                          className="mb-2"
                                                          onClick={() =>
                                                              setSelectedIdentification(
                                                                  IDENTIFICATION_TYPES.SIGNICATSCANNING
                                                              )
                                                          }>
                                                          {t(
                                                              'international_identification_signicat_scanning_button'
                                                          )}
                                                      </Button>
                                                  ));
                                            break;
                                        case IDENTIFICATION_TYPES.CARD:
                                            element = (
                                                <Button
                                                    theme="primary"
                                                    variant={
                                                        index !== 0
                                                            ? 'outline'
                                                            : undefined
                                                    }
                                                    fluid
                                                    className="mb-2"
                                                    onClick={() =>
                                                        setSelectedIdentification(
                                                            IDENTIFICATION_TYPES.CARD
                                                        )
                                                    }>
                                                    {t(
                                                        'international_identification_card_verification_button'
                                                    )}
                                                </Button>
                                            );
                                            break;
                                        case IDENTIFICATION_TYPES.PAYPAL:
                                            element = (
                                                <Button
                                                    theme="primary"
                                                    variant="outline"
                                                    fluid
                                                    className="mb-2"
                                                    onClick={() =>
                                                        setSelectedIdentification(
                                                            IDENTIFICATION_TYPES.PAYPAL
                                                        )
                                                    }
                                                    fixedIcon={true}
                                                    icon={
                                                        <PayPalLogo className="mr-1" />
                                                    }>
                                                    {t(
                                                        'international_identification_paypal_button'
                                                    )}
                                                </Button>
                                            );
                                            break;
                                        default:
                                            logger(
                                                `Invalid identification method: ${identification}`
                                            );
                                    }
                                    return (
                                        <React.Fragment key={index}>
                                            {element}
                                        </React.Fragment>
                                    );
                                }
                            )}
                    </div>
                </div>
            )}
            {selectedIdentificationGui &&
                abortController.current &&
                selectedIdentificationGui(abortController.current.signal)}
            {error && (
                <div className="px-7 py-4 text-center text-danger">
                    {t('international_identification_errorUnavailable')}
                </div>
            )}
        </ScrollableModal>
    );
};
